import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RefreshCw, Calculator, Shield } from 'lucide-react';

// Math CAPTCHA Component
function MathCaptcha({ difficulty = 'easy', onVerify, onError }) {
  const [question, setQuestion] = useState({ text: '', answer: 0 });
  const [userAnswer, setUserAnswer] = useState('');

  const generateQuestion = useCallback(() => {
    let num1, num2, operator, answer;
    
    switch (difficulty) {
      case 'hard':
        num1 = Math.floor(Math.random() * 50) + 10;
        num2 = Math.floor(Math.random() * 30) + 5;
        operator = ['+', '-', '×'][Math.floor(Math.random() * 3)];
        break;
      case 'medium':
        num1 = Math.floor(Math.random() * 20) + 5;
        num2 = Math.floor(Math.random() * 15) + 3;
        operator = ['+', '-'][Math.floor(Math.random() * 2)];
        break;
      default: // easy
        num1 = Math.floor(Math.random() * 10) + 1;
        num2 = Math.floor(Math.random() * 10) + 1;
        operator = '+';
    }
    
    switch (operator) {
      case '+':
        answer = num1 + num2;
        break;
      case '-':
        // Ensure positive result
        if (num1 < num2) [num1, num2] = [num2, num1];
        answer = num1 - num2;
        break;
      case '×':
        answer = num1 * num2;
        break;
      default:
        answer = num1 + num2;
    }
    
    setQuestion({ text: `${num1} ${operator} ${num2} = ?`, answer });
    setUserAnswer('');
  }, [difficulty]);

  useEffect(() => {
    generateQuestion();
  }, [generateQuestion]);

  const handleVerify = () => {
    if (parseInt(userAnswer) === question.answer) {
      onVerify?.({ expected: question.answer, answer: userAnswer });
      return true;
    } else {
      onError?.('Incorrect answer. Please try again.');
      generateQuestion();
      return false;
    }
  };

  return (
    <div className="space-y-3 p-4 bg-gray-50 rounded-lg border" data-testid="math-captcha">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2 text-sm font-medium">
          <Calculator className="w-4 h-4 text-indigo-600" />
          Solve to continue
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={generateQuestion}
          className="h-8 w-8"
          title="New question"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-xl font-mono font-bold text-gray-800 bg-white px-4 py-2 rounded border">
          {question.text}
        </div>
        <Input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          placeholder="Answer"
          className="w-24 text-center font-mono"
          data-testid="captcha-answer-input"
        />
      </div>
      <input type="hidden" name="captcha_answer" value={userAnswer} />
      <input type="hidden" name="captcha_expected" value={question.answer} />
    </div>
  );
}

// Google reCAPTCHA Component
function GoogleRecaptcha({ siteKey, onVerify, onError }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    
    // Load reCAPTCHA script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, [siteKey]);

  const handleExecute = async () => {
    if (!window.grecaptcha || !loaded) {
      onError?.('reCAPTCHA not loaded');
      return null;
    }
    
    try {
      const token = await window.grecaptcha.execute(siteKey, { action: 'login' });
      onVerify?.({ token });
      return token;
    } catch (error) {
      onError?.('reCAPTCHA execution failed');
      return null;
    }
  };

  return (
    <div className="space-y-2" data-testid="google-recaptcha">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Shield className="w-4 h-4" />
        Protected by Google reCAPTCHA
      </div>
      <input type="hidden" name="recaptcha_ready" value={loaded ? 'true' : 'false'} />
    </div>
  );
}

// hCaptcha Component
function HCaptcha({ siteKey, onVerify, onError }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    
    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js';
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, [siteKey]);

  const handleCallback = (token) => {
    onVerify?.({ token });
  };

  useEffect(() => {
    window.hcaptchaCallback = handleCallback;
    return () => {
      delete window.hcaptchaCallback;
    };
  }, [onVerify]);

  return (
    <div className="space-y-2" data-testid="hcaptcha">
      {loaded && (
        <div
          className="h-captcha"
          data-sitekey={siteKey}
          data-callback="hcaptchaCallback"
        />
      )}
    </div>
  );
}

// Cloudflare Turnstile Component
function CloudflareTurnstile({ siteKey, onVerify, onError }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, [siteKey]);

  const handleCallback = (token) => {
    onVerify?.({ token });
  };

  useEffect(() => {
    window.turnstileCallback = handleCallback;
    return () => {
      delete window.turnstileCallback;
    };
  }, [onVerify]);

  return (
    <div className="space-y-2" data-testid="cloudflare-turnstile">
      {loaded && (
        <div
          className="cf-turnstile"
          data-sitekey={siteKey}
          data-callback="turnstileCallback"
        />
      )}
    </div>
  );
}

// Main CAPTCHA Component
function Captcha({ loginType = 'admin', onVerify, onError }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [captchaData, setCaptchaData] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/captcha-settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch CAPTCHA settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = (data) => {
    setCaptchaData(data);
    onVerify?.(data);
  };

  const handleError = (error) => {
    onError?.(error);
  };

  // Check if CAPTCHA is enabled for this login type
  const isEnabled = () => {
    if (!settings?.enabled) return false;
    
    switch (loginType) {
      case 'superadmin':
        return settings.enable_for_superadmin;
      case 'admin':
        return settings.enable_for_admin;
      case 'staff':
        return settings.enable_for_staff;
      case 'signup':
        // Explicit off → hide; explicit on → show; missing flag → same as admin login
        if (settings.enable_for_signup === false) return false;
        if (settings.enable_for_signup === true) return true;
        return settings.enable_for_admin !== false;
      default:
        return false;
    }
  };

  if (loading) {
    return null;
  }

  if (!isEnabled()) {
    return null;
  }

  const captchaType = settings?.captcha_type;

  switch (captchaType) {
    case 'math':
      return (
        <MathCaptcha
          difficulty={settings.math_difficulty || 'easy'}
          onVerify={handleVerify}
          onError={handleError}
        />
      );
    case 'google_recaptcha':
      return (
        <GoogleRecaptcha
          siteKey={settings.google_site_key}
          onVerify={handleVerify}
          onError={handleError}
        />
      );
    case 'hcaptcha':
      return (
        <HCaptcha
          siteKey={settings.hcaptcha_site_key}
          onVerify={handleVerify}
          onError={handleError}
        />
      );
    case 'cloudflare_turnstile':
      return (
        <CloudflareTurnstile
          siteKey={settings.cloudflare_site_key}
          onVerify={handleVerify}
          onError={handleError}
        />
      );
    default:
      return null;
  }
}

export { Captcha, MathCaptcha, GoogleRecaptcha, HCaptcha, CloudflareTurnstile };
export default Captcha;
